import React from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// Third party components and hooks
import { v4 as uuidv4 } from 'uuid';

// MyFood components and hooks
import { useLocalStorage, LS_KEYS } from '../hooks/useLocalStorage';
import { useHour, useClient, useMenuItem } from '../services';

const SiteContext = React.createContext();

const INITIAL_CART = {
  total: 0,
  items: [],
};

const SiteContextProvider = ({ children }) => {
  // Hooks
  const location = useLocation();
  const { loadHoursSite } = useHour();
  const { loadLoggedClient } = useClient();
  const { listSite } = useMenuItem();
  const { lsValues, setLSItem, getLSItem, removeLSItem } = useLocalStorage(
    LS_KEYS.SITE,
  );

  // States
  const [workHours, setWorkHours] = React.useState(null);
  const [personalData, setPersonalData] = React.useState(null);
  const [menu, setMenu] = React.useState([]);
  const [isLoadingMenu, setIsLoadingMenu] = React.useState(true);
  const [inOperation, setInOperation] = React.useState(false);
  const [
    isInitiedCheckoutWithoutLogin,
    setIsInitiedCheckoutWithoutLogin,
  ] = React.useState(false);
  const [
    isFinishedCheckoutWithoutLogin,
    setIsFinishedCheckoutWithoutLogin,
  ] = React.useState(false);
  const [isCartModalOpen, setIsCartModalOpen] = React.useState(false);
  const [isPersonalDataModalOpen, setIsPersonalDataModalOpen] = React.useState(
    false,
  );
  const [needsBackToCard, setNeedsBackToCard] = React.useState(false);

  // Load cart from LS or return a empty one
  const [cart, setCart] = React.useState(() => {
    const lsCart = getLSItem('cart');
    const ret = lsCart || INITIAL_CART;
    return ret;
  });

  const getAuth = React.useCallback(() => getLSItem('auth'), [getLSItem]);

  /**
   * Load Work Hours
   */
  const loadWorkHours = React.useCallback(async () => {
    try {
      const listedWorkHours = await loadHoursSite();
      setWorkHours(listedWorkHours.data);
      setInOperation(listedWorkHours.isOpen);
    } catch (_) {
      setWorkHours(null);
    }
  }, [loadHoursSite]);

  /**
   * Load menu items
   */
  const loadMenu = React.useCallback(async () => {
    try {
      setIsLoadingMenu(true);
      setMenu(await listSite());
      setIsLoadingMenu(false);
    } catch (_) {
      setMenu([]);
      setIsLoadingMenu(false);
    }
  }, [listSite]);

  /**
   * Load client data from backend
   */
  const loadPersonalDataLoggedClient = React.useCallback(async () => {
    try {
      if (getAuth()) {
        const loadedClient = await loadLoggedClient();
        setPersonalData(loadedClient);
        return loadedClient;
      }
      setPersonalData(null);
      return null;
    } catch (error) {
      setPersonalData(null);
      return null;
    }
  }, [getAuth, loadLoggedClient]);

  /**
   * Add item(s) to cart
   */
  const addItemCart = React.useCallback(
    ({ item, itemQuantity, itemTotal }) => {
      const { total, items } = cart;

      const itemsTempCart = Array.from(
        { length: itemQuantity },
        () => item,
      ).map(i => ({ ...i, internalId: uuidv4() }));

      const newCart = {
        total: total + itemTotal,
        items: items.concat(itemsTempCart),
      };
      setCart(newCart);
      setLSItem('cart', newCart);
    },
    [cart, setLSItem],
  );

  const removeItemCart = React.useCallback(
    item => {
      const newCart = { ...cart };
      const itemIndex = newCart.items.findIndex(
        i => i.internalId === item.internalId,
      );
      const removedItem = newCart.items.splice(itemIndex, 1)[0];
      newCart.total -= removedItem.price;
      setCart(newCart);
    },
    [cart],
  );

  /**
   * Clean context cart, LS remaining
   */
  const cleanCart = React.useCallback(() => {
    removeLSItem('cart');
    setCart(INITIAL_CART);
  }, [removeLSItem]);

  /**
   * Clean cart when client not logged
   */
  React.useEffect(() => {
    if (!getAuth()) {
      cleanCart();
    }
  }, [cleanCart, getAuth]);

  /**
   * Load data for site home page
   */
  React.useEffect(() => {
    if (!location.pathname.includes('backoffice')) {
      loadWorkHours();
      loadMenu();
      loadPersonalDataLoggedClient();

      setInterval(() => {
        loadWorkHours();
        loadMenu();
      }, 1000 * 60 * 5);
    }
  }, [
    loadMenu,
    loadPersonalDataLoggedClient,
    loadWorkHours,
    location.pathname,
  ]);

  return (
    <SiteContext.Provider
      value={{
        setLSItem,
        removeLSItem,
        getLSItem,
        lsValues,
        getAuth,
        workHours,
        loadPersonalDataLoggedClient,
        personalData,
        isPersonalDataModalOpen,
        setIsPersonalDataModalOpen,
        setPersonalData,
        menu,
        loadMenu,
        isLoadingMenu,
        inOperation,
        cart,
        addItemCart,
        removeItemCart,
        isCartModalOpen,
        setIsCartModalOpen,
        needsBackToCard,
        setNeedsBackToCard,
        cleanCart,
        isInitiedCheckoutWithoutLogin,
        isFinishedCheckoutWithoutLogin,
        setIsInitiedCheckoutWithoutLogin,
        setIsFinishedCheckoutWithoutLogin,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

const useSiteContext = () => {
  const context = React.useContext(SiteContext);

  if (!context) {
    throw new Error('useSiteContext must be use with SiteContextProvider');
  }

  return context;
};

SiteContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export { useSiteContext, SiteContextProvider };
